import type { AdaptiveLogger } from "@1js/office-online-jsapi-core";
import { LogCategory, TraceLevel } from "@1js/office-online-jsapi-core";
import type { AccessTokenResponse, GraphDriveItem } from "../types";
import { getDriveItemWithUrl } from "./getDriveItemWithUrl";
import type { Action, ESSOptions, HttpHeaders, VroomApiOptions } from "./types";
import {
  applyHeaderFromTokenResponse,
  getActionString,
  getApiInfo,
  getEarlySessionStartString,
  getParamStatement,
} from "./utils";

export function getSharesUrl(
  webUrl: string,
  logger?: AdaptiveLogger
): string | undefined {
  try {
    // ASSERTTAG_IGNORE_START
    logger?.ULS.sendTraceTag(
      0x204c47db /* tag_6te51 */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Info,
      "getSharesUrl: Start constructing shares Url"
    );
    // ASSERTTAG_IGNORE_FINISH

    // encode the url for special characters in the document or the path name
    const encodedShareUrl = webUrl.replace(/[,@+$#]/g, (match: string) => {
      return encodeURIComponent(match);
    });

    // NOTE: btoa doesn't support UTF-8 encoded string. If the string contains UTF-8
    // character, btoa will throw error
    // "Failed to execute 'btoa' on 'Window': The string to be encoded contains characters outside of the Latin1 range."
    // To handle UTF-8 url:
    // first we use encodeURIComponent to get percent-encoded UTF-8,
    // then we convert the percent encodings into raw bytes which
    // can be fed into btoa.
    // Latin characters like # which are also special characters are not encoded.
    // Hence we manually encode them with encodeHash method - This is a requirement from Vroom APIs
    const base64Url = window.btoa(
      encodeHash(
        encodeURIComponent(encodedShareUrl).replace(
          /%([0-9A-F]{2})/g,
          (_match, p1) => {
            return String.fromCharCode(parseInt(p1, 16));
          }
        )
      )
    );
    let i = base64Url.length - 1;
    while (base64Url.charAt(i) === "=") {
      --i;
    }

    const encodedUrl = base64Url
      .slice(0, i + 1)
      .replace(/\+/g, "-")
      .replace(/\//g, "_");
    return `shares/u!${encodedUrl}/driveItem`;
  } catch (error) {
    // ASSERTTAG_IGNORE_START
    logger?.ULS.sendTraceTag(
      0x204c5018 /* tag_6tfay */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Error,
      `getSharesUrl: Failed to get Share Url. ${error}`
    );
    // ASSERTTAG_IGNORE_FINISH

    return undefined;
  }
}

/**
 * Manually encodes only the hash character, replaces it by %23
 * # has a special meaning in a Url
 * but the above logic of encoding and then changing to raw bytes keeps # as it is
 * because its a latin character.
 */
function encodeHash(str: string): string {
  let encodedString = str;
  if (encodedString) {
    encodedString = encodedString.replace(/#/g, "%23");
  }
  return encodedString;
}

function getHeaders(
  tokenResponse: AccessTokenResponse,
  logger: AdaptiveLogger
): HttpHeaders {
  const headerVals = {
    accept: "application/json",
    prefer: "redeemSharingLink,allow-zerobyte,manualRedirect",
  };

  // This is never actually adding the Authorization header because this functions returns the updated
  // headers instead of modifying the header values that are passed in.
  applyHeaderFromTokenResponse(
    tokenResponse,
    headerVals,
    undefined /* rpsCompactPrefix */,
    logger
  );
  return headerVals;
}

function getHeadersToFetchPACToken(
  tokenResponse: AccessTokenResponse,
  logger: AdaptiveLogger
): HttpHeaders {
  const headerVals = {
    accept: "application/json",
    prefer:
      'redeemSharingLink,allow-zerobyte,manualRedirect,pacToken=Y,pacTokenAppIds="e03a13ee-9730-4cae-8525-47559c8cf18a"',
  };

  // This is never actually adding the Authorization header because this functions returns the updated
  // headers instead of modifying the header values that are passed in.
  applyHeaderFromTokenResponse(
    tokenResponse,
    headerVals,
    undefined /* rpsCompactPrefix */,
    logger
  );
  return headerVals;
}

const getThumbnailString = (fetchThumbnails: boolean): string => {
  return fetchThumbnails ? "expand=thumbnails" : "";
};

export function getSharedItemWithParams(
  webUrl: string,
  params: Array<string>,
  vroomApiOptions: VroomApiOptions,
  forceFetch = false,
  action: Action = "open",
  fetchThumbnails = false,
  logger?: AdaptiveLogger,
  essOptions?: ESSOptions,
  enableDeprecatePreAuthUrl?: boolean,
  isRetryForSiteMove?: boolean,
  isUseWacParamsCopyEnabled?: boolean
): Promise<GraphDriveItem | undefined> {
  const { inMockMode, getAccessToken } = vroomApiOptions;

  const apiInfo = getApiInfo(webUrl, vroomApiOptions, logger);

  if (!apiInfo) {
    return Promise.reject("No apiInfo");
  }

  // This is a workaround for the issue where the openWith($select=wac) parameter is not supported in the new item creation flow
  // This can be removed once the feature gate FeatureGate_UseWacParamsCopy is fully rolled out
  if (!isUseWacParamsCopyEnabled && action === "editnew") {
    const index = params.indexOf("openWith($select=wac)");
    if (index !== -1) {
      params[index] = "openWith";
    }
  }

  // this indicates site has moved, SPO has sent back a fully formed url in the response
  // Generating shares url must be skipped in this case
  if (isRetryForSiteMove) {
    return getDriveItemWithUrl(
      webUrl,
      getAccessToken,
      apiInfo.tokenType,
      apiInfo.resourceUrl,
      forceFetch,
      logger,
      enableDeprecatePreAuthUrl ? getHeadersToFetchPACToken : getHeaders,
      isRetryForSiteMove
    );
  }
  // if url is encoded, use webUrl as shareUrl, else generate share url
  const shareUrl = getSharesUrl(webUrl, logger);

  if (!shareUrl) {
    return Promise.reject("No shareUrl");
  }

  const currentDate = new Date();
  currentDate.setSeconds(0, 0);

  const queryParams = [
    getParamStatement(params),
    getActionString(action),
    getThumbnailString(fetchThumbnails),
    ...getEarlySessionStartString(essOptions),
  ]
    .filter((x) => !!x)
    .join("&");

  const endpoint = inMockMode
    ? `shares?itemUrl=${encodeURIComponent(
        shareUrl
      )}&currentDate=${currentDate.toISOString()}`
    : `${shareUrl}?${queryParams}`;

  const url = `${apiInfo.baseUrl}/${endpoint}`;

  return getDriveItemWithUrl(
    url,
    getAccessToken,
    apiInfo.tokenType,
    apiInfo.resourceUrl,
    forceFetch,
    logger,
    enableDeprecatePreAuthUrl ? getHeadersToFetchPACToken : getHeaders
  );
}
